import React from 'react';
import CustomTextField from '../../components/FormComponents/CustomTextField';
import RadioSelect from '../../components/FormComponents/RadioSelect';
import {
    Grid,
    Box,
    FormHelperText,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import CustomDatePicker from '../../components/FormComponents/CustomDatePicker';
import { militaryBranches, militaryStatus, refferalTypes } from '../../staticDataSet';
import AddressAutoComplete from '../../components/FormComponents/AddressAutoComplete';
import CustomDropdown from '../../components/FormComponents/CustomDropdown';
import { militaryCategoriesRequest } from '../../remote/militaryCategoriesRequest';


export default function UserForm({ session, formData, setFormData, formType, error }) {

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    return (
        <Grid container spacing={2}>
            {/* Username / Email */}
            <Grid item xs={12} sm={6}>
                <CustomTextField
                    formData={formData}
                    field="username"
                    type="email"
                    handleChange={handleChange}
                    errors={error}
                    readOnly={formType === "edit"}
                    required={formType === "registration"}
                    label="Email Address"
                    error={!!error.username}
                    helperText={error.username}
                />
            </Grid>

            {/* Referred By */}
            {formType === 'registration' && (
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!error.referredBy} sx={{ mt: 1 }}>
                        <InputLabel id="referred-by-label">Referred By</InputLabel>
                        <Select
                            labelId="referred-by-label"
                            value={formData?.referredBy || ""}
                            onChange={(e) => handleChange("referredBy", e.target.value)}
                            label="Referred By"
                            size="medium"
                            variant="standard"
                        >
                            <MenuItem value="" disabled>Select Referral</MenuItem>
                            {refferalTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.referredBy && (
                            <FormHelperText>{error.referredBy}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
            )}

            {/* Anonymous Toggle (Edit Only) */}
            {formType === 'edit' && (
                <Grid item xs={12} sm={6}>
                    <FormHelperText>Search Appearance</FormHelperText>
                    <RadioSelect
                        value={formData?.isAnonymous}
                        onChange={(val) => handleChange("isAnonymous", val)}
                        label={formData?.isAnonymous ? "Will Not Appear in Searches" : "Will Appear in Searches"}
                    />
                </Grid>
            )}

            {/* Password Fields */}
            <Grid item xs={12} sm={6}>
                {formType === 'edit' && (<FormHelperText>If you would like to change your password, you can do it here.</FormHelperText>)}
                <CustomTextField
                    formData={formData}
                    field="password"
                    type="password"
                    handleChange={handleChange}
                    errors={error}
                    required={formType === "registration"}

                    label="Password"
                    error={!!error.password}
                    helperText="Password must be 8-16 characters long, include at least one uppercase letter, and one special character."
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: formType === 'registration' ? 3 : 0, mt: formType === 'edit' ? 3 : 0 }}>
                <CustomTextField
                    formData={formData}
                    field="confirmPassword"
                    type="password"
                    handleChange={handleChange}
                    errors={error}
                    disabled={formType === "edit" && !formData?.password}
                    required={!!formData?.password}
                    label="Confirm Password"
                    error={!!error.confirmPassword}
                    helperText={error.confirmPassword}
                />
            </Grid>

            {/* First / Last Name */}
            <Grid item xs={12} sm={6}>
                <CustomTextField
                    formData={formData}
                    field="firstName"
                    handleChange={handleChange}
                    errors={error}
                    required
                    label="First Name"
                    error={!!error.firstName}
                    helperText={error.firstName}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <CustomTextField
                    formData={formData}
                    field="lastName"
                    handleChange={handleChange}
                    errors={error}
                    required
                    label="Last Name"
                    error={!!error.lastName}
                    helperText={error.lastName}
                />
            </Grid>

            {/* Address Section */}
            <Grid item xs={12}>
                <AddressAutoComplete
                    address1={formData.addressLine1}
                    setAddress1={(value) => handleChange("addressLine1", value)}
                    setCity={(value) => handleChange("city", value)}
                    setState={(value) => handleChange("state", value)}
                    setCountry={(value) => handleChange("country", value)}
                    setZip={(value) => handleChange("zipCode", value)}
                    error={error?.addressLine1}
                    helperText={error?.addressLine1}
                    required
                />
                <Box mt={2}>
                    <CustomTextField
                        formData={formData}
                        field="addressLine2"
                        handleChange={handleChange}
                        errors={error}
                        label="Address Line 2"
                    />
                </Box>
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={4}>
                        <CustomTextField
                            formData={formData}
                            field="city"
                            handleChange={handleChange}
                            errors={error}
                            label="City"
                            required
                            error={!!error.city}
                            helperText={error.city}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CustomTextField
                            formData={formData}
                            field="state"
                            handleChange={handleChange}
                            errors={error}
                            label="State"
                            required
                            error={!!error.state}
                            helperText={error.state}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CustomTextField
                            formData={formData}
                            field="zipCode"
                            handleChange={handleChange}
                            errors={error}
                            label="ZIP Code"
                            required
                            error={!!error.zipCode}
                            helperText={error.zipCode}
                        />
                    </Grid>
                </Grid>
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12} sm={4}>
                <CustomTextField
                    formData={formData}
                    field="primaryPhone"
                    handleChange={handleChange}
                    required
                    label="Primary Phone"
                    error={!!error.primaryPhone}
                    helperText={error.primaryPhone}
                    type="tel"
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <CustomTextField
                    formData={formData}
                    field="alternatePhone"
                    handleChange={handleChange}
                    label="Alternate Phone"
                    type="tel"
                />
            </Grid>

            {/* Military Service */}
            <Grid item xs={12}>
                <RadioSelect
                    value={formData?.militaryService}
                    onChange={(val) => handleChange("militaryService", val)}
                    label="Military Service"
                />
            </Grid>

            {formData.militaryService && (
                <>
                    {/* Branch */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!error.militaryServiceBranch}>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={formData.militaryServiceBranch}
                                onChange={(e) => handleChange("militaryServiceBranch", e.target.value)}
                                label="Branch"
                                variant="standard"
                            >
                                <MenuItem value="" disabled>Select Branch</MenuItem>
                                {militaryBranches.map(branch => (
                                    <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                                ))}
                            </Select>
                            {error.militaryServiceBranch && <FormHelperText>{error.militaryServiceBranch}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!error.militaryServiceStatus}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.militaryServiceStatus}
                                onChange={(e) => handleChange("militaryServiceStatus", e.target.value)}
                                label="Status"
                                variant="standard"
                            >
                                <MenuItem value="" disabled>Select Status</MenuItem>
                                {militaryStatus.map(status => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                            {error.militaryServiceStatus && <FormHelperText>{error.militaryServiceStatus}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    {/* Start / End Dates */}
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={formData.militaryServiceStartDate}
                            setSelectedDate={(val) => handleChange("militaryServiceStartDate", val)}
                            label="Start Date"
                        />
                        {error.militaryServiceStartDate && <FormHelperText>{error.militaryServiceStartDate}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={formData.militaryServiceEndDate}
                            setSelectedDate={(val) => handleChange("militaryServiceEndDate", val)}
                            label="End Date"
                        />
                        {error.militaryServiceEndDate && <FormHelperText>{error.militaryServiceEndDate}</FormHelperText>}
                    </Grid>

                    {/* Military Category */}
                    {session && formType === 'edit' && (
                        <Grid item xs={12} sm={6}> 
                            <CustomDropdown
                                session={session}
                                value={formData?.militaryCategoryId}
                                setEntity={(category) => handleChange("militaryCategoryId", category)}
                                style={{ width: "100%" }}
                                label="Military Job Category"
                                request={militaryCategoriesRequest}
                                entityName="militaryCategoryName"
                                entityIdField="militaryCategoryId"
                                error={!!error.militaryCategoryId}
                            />
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    );
}
